import { useMutation, useQueryClient } from 'react-query';
import { Dispatch } from 'react';
import api from 'api';
import {
  ArrearsState,
  ArrearsAction,
  ArrearComponent,
  DeferArrearsCommit,
  DeferredArrearsRow,
} from '../types';
import { queryKeys } from '../data';
import {
  getBulkDeleteArrearsMutationPayload,
  getBulkPreviewArrearsMutationPayload,
  validateArrearsForm,
} from '../utils';
import { getDeletedMonths, getMonthsForPreview, getEditedMonths } from '../state/selectors';
import { useModal } from 'components/ui/Modals';
import { useState } from 'react';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { AppError } from 'utils/AppError';

export const useArrearsMutations = (
  dispatch: Dispatch<ArrearsAction>,
  state: ArrearsState,
  orgId: string,
  userId: string,
  payrollMonth: string,
  loggedInUserId: string,
  onDeferCommit?: DeferArrearsCommit,
) => {
  const [errorMessage, setErrorMessage] = useState('');
  const queryClient = useQueryClient();
  const { closeModal } = useModal();

  // Bulk Delete Mutation
  const { mutateAsync: bulkDeleteArrearsMutate, isLoading: isDeletingArrears } = useMutation({
    mutationFn: api.arrears.mutations.bulkDeleteArrears,
    mutationKey: [queryKeys.BULK_DELETE_ARREARS],
    onSuccess: (data) => {
      const deletedIds = data?.payrollBulkDeleteArrears?.data?.deletionResults
        ?.filter((result) => !result.errorMessage)
        .map((result) => result.deletedArrearId);

      if (deletedIds && deletedIds.length > 0) {
        dispatch({ type: 'DELETE_COMPLETED', payload: { arrearIds: deletedIds } });
      }
    },
  });

  // Bulk Preview Mutation
  const { mutateAsync: bulkPreviewArrearsMutate, isLoading: isComputingArrears } = useMutation({
    mutationFn: api.arrears.mutations.bulkPreviewArrears,
    mutationKey: [queryKeys.BULK_PREVIEW_ARREARS],
    onSuccess: (data) => {
      const arrears = data?.payrollBulkPreviewArrears?.data?.arrears ?? [];
      const requestId = data?.payrollBulkPreviewArrears?.data?.requestId ?? '';

      dispatch({
        type: 'PREVIEW_COMPUTED',
        payload: { arrears, requestId },
      });
    },
  });

  // Save Arrears Mutation
  const { mutateAsync: saveArrearsMutate, isLoading: isSavingArrears } = useMutation({
    mutationFn: api.arrears.mutations.saveArrears,
    mutationKey: [queryKeys.SAVE_ARREARS],
    onSuccess: () => {
      dispatch({ type: 'CLEAR_PREVIEW_CACHE' });
    },
  });

  /**
   * Handle compute compliance arrears (delete + preview)
   */
  const handleCompute = async (): Promise<string | undefined> => {
    const errorMessage = validateArrearsForm(state);
    setErrorMessage(errorMessage);
    if (errorMessage) {
      return;
    }
    try {
      const monthsForPreview = getMonthsForPreview(state);
      if (monthsForPreview.length > 0) {
        const res = await bulkPreviewArrearsMutate(
          getBulkPreviewArrearsMutationPayload({
            orgId,
            requesterPeopleId: loggedInUserId,
            arrearsBreakdown: state.months,
            editHistory: state.editHistory,
            payrollMonth,
            userId,
          }),
        );
        if (res?.payrollBulkPreviewArrears?.success) {
          if (res?.payrollBulkPreviewArrears?.data?.requestId) {
            return res.payrollBulkPreviewArrears.data.requestId;
          } else {
            throw new Error('Failed to compute arrears');
          }
        } else {
          const combinedErrors = res?.payrollBulkPreviewArrears?.data?.errors?.join(', ');
          throw new Error(
            res?.payrollBulkPreviewArrears?.message + ': ' + combinedErrors ||
              'Failed to compute arrears',
          );
        }
      }
    } catch (error) {
      console.error('Error computing arrears:', error);
      throw error;
    }
  };

  /**
   * Handle save arrears (compute if needed, then save)
   */
  const handleSave = async (): Promise<void> => {
    const errorMessage = validateArrearsForm(state);
    setErrorMessage(errorMessage);
    if (errorMessage) {
      return;
    }
    try {
      let requestId = state.previewCache?.requestId;
      const hasUpdatedMonths = getEditedMonths(state).length > 0;

      if (hasUpdatedMonths && !requestId) {
        requestId = await handleCompute();
      }

      const deletedMonthIds = getDeletedMonths(state);

      if (onDeferCommit) {
        const userArrearRequests = getBulkPreviewArrearsMutationPayload({
          orgId,
          requesterPeopleId: loggedInUserId,
          arrearsBreakdown: state.months,
          editHistory: state.editHistory,
          payrollMonth,
          userId,
        }).userArrearRequests;

        if (userArrearRequests.length > 0 || deletedMonthIds.length > 0) {
          const rowsByComponent = new Map<string, DeferredArrearsRow>();
          Object.keys(state.months).forEach((arrearId) => {
            if (state.editHistory[arrearId]?.isDeleted) {
              return;
            }
            state.months[arrearId]?.components.forEach((component) => {
              const existing = rowsByComponent.get(component.componentId);
              const amount = Number(component.amount ?? 0);
              if (existing) {
                existing.arrearAmount += amount;
              } else {
                rowsByComponent.set(component.componentId, {
                  id: component.componentId,
                  component: component.componentName,
                  arrearAmount: amount,
                  sdiType: component.sdiType,
                });
              }
            });
          });

          const sumComponents = (components: ArrearComponent[] | undefined) =>
            (components ?? []).reduce(
              (componentSum, component) => componentSum + Number(component.amount ?? 0),
              0,
            );

          const rows = Array.from(rowsByComponent.values());

          const totalAmount = rows.reduce((sum, row) => sum + row.arrearAmount, 0);

          const previousAmount = Object.keys(state.months).reduce(
            (sum, arrearId) =>
              sum +
              sumComponents(
                (state.editHistory[arrearId]?.originalData ?? state.months[arrearId])?.components,
              ),
            0,
          );

          onDeferCommit({
            request: {
              userArrearRequests,
              deletedArrearIds: deletedMonthIds,
              previewRequestId: requestId ?? null,
              totalAmount,
              previousAmount,
            },
            rows,
          });
        }
        closeModal();
        return;
      }

      let errorMessage = '';

      // First, delete marked months if any
      if (deletedMonthIds.length > 0) {
        const response = await bulkDeleteArrearsMutate(
          getBulkDeleteArrearsMutationPayload({
            orgId,
            editHistory: state.editHistory,
          }),
        );

        const failedMonths = response?.payrollBulkDeleteArrears?.data?.deletionResults
          ?.filter((result) => result.errorMessage)
          .map((result) => result.sourceMonth);

        if (failedMonths.length > 0) {
          errorMessage =
            errorMessage + 'Failed to delete arrears for months: ' + failedMonths.join(', ') + '\n';
        } else if (response?.payrollBulkDeleteArrears?.success === false) {
          errorMessage =
            errorMessage +
            (response?.payrollBulkDeleteArrears?.message || 'Failed to delete arrears');
        }
      }

      if (hasUpdatedMonths && requestId) {
        // Save arrears
        const response = await saveArrearsMutate({
          merchantId: orgId,
          tenantId: orgId,
          requestId: requestId ?? '',
          requestBy: loggedInUserId,
          userIds: [userId],
        });
        const failedMonths = response?.payrollSaveArrears?.data?.processingResults.filter(
          (item) => item.errorMessage,
        );
        if (failedMonths.length > 0) {
          errorMessage =
            errorMessage + 'Failed to save arrears for months: ' + failedMonths.join(', ') + '\n';
        } else if (response?.payrollSaveArrears?.success === false) {
          errorMessage =
            errorMessage + (response?.payrollSaveArrears?.message || 'Failed to save arrears');
        }
      }
      if (errorMessage) {
        throw new Error(errorMessage);
      }
      closeModal();
      queryClient.invalidateQueries([queryKeys.FETCH_ARREARS]);
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else if (error instanceof AppError) {
        setErrorMessage(parseGraphqlErrorFromExtensions(error));
      } else {
        setErrorMessage('Something went wrong, please try again');
      }
    }
  };

  /**
   * Clear preview cache when edits are made
   */
  const clearPreviewCache = () => {
    if (state.previewCache?.requestId) {
      dispatch({ type: 'CLEAR_PREVIEW_CACHE' });
    }
  };

  return {
    handleCompute,
    handleSave,
    clearPreviewCache,
    isLoading: isDeletingArrears || isComputingArrears || isSavingArrears,
    isDeletingArrears,
    isComputingArrears,
    isSavingArrears,
    errorMessage,
    setErrorMessage,
  };
};
